/**
 * MFExplainPanel — Phase C
 *
 * Renders a full transparency breakdown of a fund's scoring.
 * Used inline in MFCategoryRankings (compact=true) and as standalone view.
 *
 * Props:
 *   data    — explain response from GET /mf-intel/explain/:schemeCode
 *   compact — if true, renders condensed version without full header
 */

import React, { useState } from 'react'
import { TrendingUp, TrendingDown, Shield, Activity, BarChart2, ChevronDown, ChevronRight } from 'lucide-react'

const fmt = (v, d = 2, fallback = '—') =>
  v != null && !isNaN(v) ? Number(v).toFixed(d) : fallback

const pctColor = (v) =>
  v == null ? 'text-gray-400' : v >= 0 ? 'text-success-600' : 'text-danger-600'

const ConfBadge = ({ level }) => {
  const s = { HIGH: 'bg-success-100 text-success-700', MEDIUM: 'bg-amber-100 text-amber-700', LOW: 'bg-danger-100 text-danger-700' }
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${s[level] || s.LOW}`}>{level}</span>
}

const ScoreCell = ({ label, score, weight, contribution, clamp, rawValue, unit = '', note, excluded, children }) => {
  const [open, setOpen] = useState(false)
  const barColor = score == null ? 'bg-gray-200'
    : score >= 70 ? 'bg-success-400'
    : score >= 50 ? 'bg-amber-400'
    : 'bg-danger-400'

  return (
    <div className={`border rounded-lg overflow-hidden ${excluded ? 'opacity-50' : ''}`}>
      <div
        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
          <span className="text-xs font-semibold text-gray-700 w-28 shrink-0">{label}</span>
          <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${score ?? 0}%` }} />
          </div>
          <span className="text-xs font-bold tabular-nums w-10">{excluded ? 'excl.' : fmt(score, 1)}</span>
        </div>
        <div className="flex items-center gap-4 text-[10px] text-gray-500 shrink-0">
          <span>wt <span className="font-bold text-gray-700">{weight}%</span></span>
          <span className={`font-bold tabular-nums ${contribution > 0 ? 'text-primary-600' : 'text-gray-400'}`}>
            +{fmt(contribution, 2)}
          </span>
        </div>
      </div>
      {open && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-600 space-y-1.5">
          {excluded && <p className="text-amber-700 font-medium">⚠️ Excluded from composite — weight redistributed to other scores</p>}
          {rawValue != null && (
            <div className="flex gap-2">
              <span className="text-gray-400 w-20 shrink-0">Raw value</span>
              <span className="font-mono font-semibold">{fmt(rawValue, 3)}{unit}</span>
            </div>
          )}
          {clamp && (
            <div className="flex gap-2">
              <span className="text-gray-400 w-20 shrink-0">Clamp</span>
              <span className="font-mono">[{clamp.min}, {clamp.max}]{unit}</span>
            </div>
          )}
          {children}
          {note && <p className="text-gray-400 italic">{note}</p>}
        </div>
      )}
    </div>
  )
}

const Row = ({ label, value, mono = false, color }) => (
  <div className="flex items-baseline gap-2 text-xs">
    <span className="text-gray-400 w-32 shrink-0">{label}</span>
    <span className={`${mono ? 'font-mono' : 'font-semibold'} ${color || 'text-gray-800'}`}>{value}</span>
  </div>
)

// ── Main panel ─────────────────────────────────────────────────────────────────

const MFExplainPanel = ({ data, compact = false }) => {
  if (!data || data.error) return (
    <div className="text-xs text-danger-500 py-2">{data?.error || 'No data available'}</div>
  )

  const { scheme, navHistory, returns, volatility, sharpe, drawdown, momentum,
          consistency, categoryRank, scoreBreakdown: sb, composite, relativeScoring,
          storedScore, confidence, confidenceChecks, confidenceReasons } = data

  // Use structured checks if available, fall back to legacy reasons array
  const checks = confidenceChecks?.length
    ? confidenceChecks
    : (confidenceReasons || []).map(r => ({ type: 'warn', message: r }))

  const sectionClass = compact ? 'space-y-2' : 'space-y-3'
  const labelClass = compact ? 'text-[11px] font-bold text-gray-500 uppercase tracking-wider' : 'text-xs font-bold text-gray-500 uppercase tracking-wider'

  return (
    <div className={`${sectionClass} font-sans`}>

      {/* ── Header ── */}
      {!compact && (
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-100 rounded-xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-900">{scheme.name}</h2>
              <p className="text-xs text-gray-500">{scheme.amc} · {scheme.category} › {scheme.subCategory}</p>
              <p className="text-xs text-gray-400">{scheme.planType} · {scheme.option}</p>
            </div>
            <div className="text-right space-y-1">
              <ConfBadge level={confidence} />
              {storedScore && (
                <p className="text-xs text-gray-500">
                  Rank <span className="font-bold">#{storedScore.rank}</span> · {storedScore.percentile}p
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── NAV History ── */}
      <div>
        <p className={labelClass}>NAV History</p>
        <div className="mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { label: 'Records',    value: navHistory.count?.toLocaleString() },
            { label: 'Years',      value: `${navHistory.yearsOfData} yr` },
            { label: 'First date', value: navHistory.firstDate },
            { label: 'Last NAV',   value: navHistory.lastNav != null ? `₹${navHistory.lastNav.toFixed(2)}` : '—' },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border border-gray-100 rounded-lg px-3 py-2">
              <p className="text-[10px] text-gray-400">{label}</p>
              <p className="text-xs font-bold text-gray-800">{value ?? '—'}</p>
            </div>
          ))}
        </div>
        {navHistory.hasDataGap && (
          <p className="text-[11px] text-amber-600 mt-1">⚠️ Data gap detected: {navHistory.maxGapDays} days around {navHistory.maxGapStart}</p>
        )}
      </div>

      {/* ── Returns ── */}
      <div>
        <p className={labelClass}>Returns</p>
        <div className="mt-1.5 grid grid-cols-3 md:grid-cols-6 gap-2">
          {[
            { label: '1M',      val: returns.r1M },
            { label: '3M',      val: returns.r3M },
            { label: '6M',      val: returns.r6M },
            { label: '1Y',      val: returns.r1Y },
            { label: '3Y CAGR', val: returns.r3YCAGR },
            { label: '5Y CAGR', val: returns.r5YCAGR },
          ].map(({ label, val }) => (
            <div key={label} className="bg-white border border-gray-100 rounded-lg px-3 py-2 text-center">
              <p className="text-[10px] text-gray-400">{label}</p>
              <p className={`text-xs font-bold ${val != null ? (val >= 0 ? 'text-success-700' : 'text-danger-700') : 'text-gray-300'}`}>
                {val != null ? `${val >= 0 ? '+' : ''}${val}%` : '—'}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Category Rank ── */}
      {categoryRank && (
        <div>
          <p className={labelClass}>Category Context</p>
          <div className="mt-1.5 grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { label: 'Rank',         value: categoryRank.rank ? `#${categoryRank.rank}` : '—' },
              { label: 'Percentile',   value: categoryRank.percentile ? `${categoryRank.percentile}p` : '—' },
              { label: 'Category size', value: categoryRank.categorySize ?? '—' },
              { label: 'Peer median 1Y', value: categoryRank.peerReturn1YMedian != null ? `${categoryRank.peerReturn1YMedian}%` : '—' },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white border border-gray-100 rounded-lg px-3 py-2">
                <p className="text-[10px] text-gray-400">{label}</p>
                <p className="text-xs font-bold text-gray-800">{String(value)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Score Breakdown ── */}
      <div>
        <div className="flex items-center justify-between">
          <p className={labelClass}>Score Breakdown</p>
          {composite && (
            <span className="text-sm font-bold text-primary-700">
              {composite.composite} / 100
              {composite.missingWeight > 0 && (
                <span className="text-[10px] text-gray-400 ml-1">({composite.missingWeight}% redistributed)</span>
              )}
            </span>
          )}
        </div>
        <div className="mt-1.5 space-y-1.5">
          {sb && [
            { key: 'returns1Y',   label: 'Returns 1Y',   s: sb.returns1Y },
            { key: 'returns3Y',   label: 'Returns 3Y',   s: sb.returns3Y },
            { key: 'momentum',    label: 'Momentum',     s: sb.momentum },
            { key: 'consistency', label: 'Consistency',  s: sb.consistency },
            { key: 'sharpe',      label: 'Sharpe',       s: sb.sharpe },
            { key: 'volatility',  label: 'Volatility',   s: sb.volatility },
            { key: 'drawdown',    label: 'DD Protection', s: sb.drawdown },
            { key: 'categoryRank', label: 'Category Rank', s: sb.categoryRank },
          ].map(({ key, label, s }) => (
            <ScoreCell
              key={key}
              label={label}
              score={s.normalisedScore}
              weight={s.weight}
              contribution={s.contribution}
              clamp={s.clamp}
              rawValue={s.rawValue ?? s.rawValue3YCAGR ?? s.momentumRaw ?? s.consistencyPct ?? s.sharpeRatio ?? s.annualisedVol ?? s.maxDrawdownPct ?? s.percentile}
              unit={key === 'returns1Y' || key === 'returns3Y' || key === 'momentum' || key === 'consistency' ? '%' : key === 'volatility' ? '% p.a.' : ''}
              note={s.note ?? s.fallbackReason ?? s.exclusionReason}
              excluded={s.excluded}
            >
              {/* Extra detail per sub-score */}
              {key === 'sharpe' && !s.excluded && s.calculation && (
                <div className="flex gap-2"><span className="text-gray-400 w-20 shrink-0">Calculation</span><span className="font-mono">{s.calculation}</span></div>
              )}
              {key === 'momentum' && s.return3M != null && (
                <div className="flex gap-2"><span className="text-gray-400 w-20 shrink-0">3M vs 6M/2</span><span className="font-mono">{s.return3M}% − {s.return6M}/2 = {s.momentumRaw}%</span></div>
              )}
              {key === 'consistency' && s.totalWindows && (
                <div className="flex gap-2"><span className="text-gray-400 w-20 shrink-0">Windows</span><span className="font-mono">{s.totalWindows} total, 63-day each</span></div>
              )}
              {key === 'categoryRank' && s.peerReturn1YMedian != null && (
                <div className="flex gap-2"><span className="text-gray-400 w-20 shrink-0">vs peer median</span><span className={`font-mono ${(s.myReturn1Y ?? 0) >= (s.peerReturn1YMedian ?? 0) ? 'text-success-600' : 'text-danger-600'}`}>my {s.myReturn1Y}% vs {s.peerReturn1YMedian}% median</span></div>
              )}
            </ScoreCell>
          ))}
        </div>
      </div>

      {/* ── Relative Scoring (Phase D) ───────────────────────────────────── */}
      {relativeScoring && (
        <div>
          <p className={labelClass}>Category-Relative Score (Phase D)</p>
          {relativeScoring.available === false ? (
            <p className="text-xs text-amber-600 mt-1.5 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              ⚠️ {relativeScoring.reason}
            </p>
          ) : (
            <div className="mt-1.5 space-y-2">
              {/* Blend formula */}
              <div className="bg-white border border-primary-100 rounded-lg px-3 py-2 text-xs">
                <p className="text-gray-400 mb-1">Composite formula (Phase D blend)</p>
                <p className="font-mono text-primary-700">{relativeScoring.blendFormula}</p>
                <div className="flex gap-4 mt-1 text-[11px]">
                  <span>Absolute: <strong>{relativeScoring.absoluteScore?.toFixed(1)}</strong></span>
                  <span>Relative: <strong>{relativeScoring.relativeScore?.toFixed(1) ?? '—'}</strong></span>
                  <span>→ Final: <strong className="text-primary-700">{relativeScoring.blendedComposite?.toFixed(1)}</strong></span>
                </div>
              </div>

              {/* Category medians */}
              <div>
                <p className="text-[10px] font-semibold text-gray-400 mb-1">Peer Medians ({relativeScoring.categoryMedians?.peerCount} funds)</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                  {[
                    { label: 'Median 3Y CAGR', value: relativeScoring.categoryMedians?.median3YCAGR, unit: '%' },
                    { label: 'Median Sharpe',  value: relativeScoring.categoryMedians?.medianSharpe,  unit: '' },
                    { label: 'Median Max DD',  value: relativeScoring.categoryMedians?.medianDrawdown, unit: '%' },
                    { label: 'Median Vol',     value: relativeScoring.categoryMedians?.medianVol,     unit: '%' },
                  ].map(({ label, value, unit }) => (
                    <div key={label} className="bg-gray-50 border border-gray-100 rounded px-2 py-1.5">
                      <p className="text-[10px] text-gray-400">{label}</p>
                      <p className="text-xs font-bold">{value != null ? `${Number(value).toFixed(2)}${unit}` : '—'}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Excess sub-scores */}
              <div className="space-y-1.5">
                {[
                  { label: 'Excess 3Y CAGR', excess: relativeScoring.excessReturns3Y, score: relativeScoring.excessReturns3YScore, bound: 15, unit: '%', wt: 40 },
                  { label: 'Excess Sharpe',  excess: relativeScoring.excessSharpe,    score: relativeScoring.excessSharpeScore,    bound: 1.0, unit: '', wt: 35 },
                  { label: 'DD Protection',  excess: relativeScoring.excessDrawdown,  score: relativeScoring.excessDrawdownScore,  bound: 20,  unit: '%', wt: 25 },
                ].map(({ label, excess, score, bound, unit, wt }) => {
                  const isGood = excess != null && excess >= 0
                  return (
                    <div key={label} className="flex items-center gap-3 bg-white border border-gray-100 rounded-lg px-3 py-2">
                      <span className="text-xs text-gray-500 w-28 shrink-0">{label}</span>
                      <span className={`text-xs font-mono font-bold w-16 ${excess == null ? 'text-gray-300' : isGood ? 'text-success-600' : 'text-danger-600'}`}>
                        {excess != null ? `${excess >= 0 ? '+' : ''}${Number(excess).toFixed(2)}${unit}` : '—'}
                      </span>
                      <span className="text-[10px] text-gray-400">vs median, clamp ±{bound}{unit}</span>
                      <div className="ml-auto flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${(score ?? 50) >= 50 ? 'bg-success-400' : 'bg-danger-400'}`} style={{ width: `${score ?? 0}%` }} />
                        </div>
                        <span className="text-xs font-bold tabular-nums w-8">{score?.toFixed(1) ?? '—'}</span>
                        <span className="text-[10px] text-gray-400">wt {wt}%</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Confidence (Phase E Engine) ───────────────────────────────────── */}
      <div>
        <p className={labelClass}>Confidence Engine</p>
        <div className="mt-1.5 bg-white border border-gray-100 rounded-lg p-3 space-y-1.5">
          <div className="flex items-center gap-2 mb-3">
            <ConfBadge level={confidence} />
            <span className="text-xs text-gray-500">
              {confidence === 'HIGH'
                ? 'All critical quality checks passed'
                : confidence === 'MEDIUM'
                ? 'Minor data limitations — scores are usable but with caveats'
                : 'Significant limitations — interpret scores with caution'}
            </span>
          </div>
          {checks.map((c, i) => {
            const icon  = c.type === 'pass' ? '✓' : c.type === 'fail' ? '✗' : '⚠'
            const color = c.type === 'pass' ? 'text-success-700' : c.type === 'fail' ? 'text-danger-700' : 'text-amber-700'
            const bg    = c.type === 'pass' ? 'bg-success-50/50' : c.type === 'fail' ? 'bg-danger-50/50' : 'bg-amber-50/50'
            return (
              <div key={i} className={`flex items-start gap-2 text-xs px-2 py-1 rounded ${bg}`}>
                <span className={`font-bold mt-0.5 shrink-0 ${color}`}>{icon}</span>
                <span className={color}>{c.message}</span>
              </div>
            )
          })}
        </div>
      </div>

    </div>
  )
}

export default MFExplainPanel
